/**
 * Which train he is actually on, argued from where the phone has been.
 *
 * He says which train he is on by pinning one, and that declaration wins. This is the second
 * opinion: the phone knows the last moment it could see him standing on his own platform, and a
 * train that left before that moment is one he cannot be sitting on. The earliest departure at
 * or after that sighting is the only train he could have caught without letting one go.
 *
 * Deliberately narrow. It says nothing at all when the platform was never seen — underground at
 * Civic Center there is no fix to reason from, and a guess made without evidence is worse than
 * admitting there is none.
 */

import type { CommutePlan } from "../shared/types";

/** A fix vaguer than this cannot place anyone on a platform. */
export const SIGHTING_ACCURACY_METERS = 100;

/** How close to the boarding station counts as standing at it. */
export const SIGHTING_METERS = 150;

export interface PlatformSighting {
	/** When the fix was taken, not when it was reported. */
	atMs: number;
	metersFromBoarding: number;
	accuracyMeters: number | null;
}

/** Whether this fix is good enough to say he was on the platform at that moment. */
export function isPlatformSighting(sighting: PlatformSighting): boolean {
	if (sighting.accuracyMeters != null && sighting.accuracyMeters > SIGHTING_ACCURACY_METERS) {
		return false;
	}
	return sighting.metersFromBoarding <= SIGHTING_METERS;
}

/**
 * The train the evidence points at, or null when there is none.
 *
 * `lastSeenAtPlatformMs` is the newest moment the phone had him at his own boarding station.
 */
export function guessBoardedTrain(options: CommutePlan[], lastSeenAtPlatformMs: number | null, nowMs: number): string | null {
	if (lastSeenAtPlatformMs == null) {
		return null;
	}
	let best: CommutePlan | null = null;
	for (const plan of options) {
		const departsMs = plan.boarding.predictedMs;
		// Still to come: he is not on it yet, whatever the deck is showing.
		if (departsMs > nowMs || departsMs < lastSeenAtPlatformMs) {
			continue;
		}
		if (best == null || departsMs < best.boarding.predictedMs) {
			best = plan;
		}
	}
	return best?.id ?? null;
}

/**
 * The same guess, held to.
 *
 * `guessBoardedTrain` answers from the shortlist as it stands, and the shortlist moves under it:
 * departed trains fall off as the ride goes on, so the same evidence names a different train
 * twenty minutes in. Seen on 2026-08-26 — right at 15:41, wrong by 16:05, having never left the
 * seat. Keeping the first answer is what makes this a claim about the train he got on rather
 * than a rolling opinion about the timetable.
 *
 * Stepping back off the train drops it, because then he is telling the app it was wrong.
 */
export function latchBoardedTrain(
	latched: string | null,
	boarded: boolean,
	options: CommutePlan[],
	lastSeenAtPlatformMs: number | null,
	nowMs: number,
): string | null {
	if (!boarded) {
		return null;
	}
	return latched ?? guessBoardedTrain(options, lastSeenAtPlatformMs, nowMs);
}
